import * as React from 'react';
import { DatePicker, IDatePicker, IDatePickerProps } from '@uifabric/date-time/lib/DatePicker';
import {
    asDate,
    dateTimeFormatter,
    endOfDay,
    isEqual,
    isWithinRange,
    MAX_JAVASCRIPT_DATE,
    MAX_OUTLOOK_DATE,
    MIN_JAVASCRIPT_DATE,
    MIN_OUTLOOK_DATE,
    owaDate,
    OwaDate,
    parseUserDate,
    startOfDay,
} from 'owa-datetime';
import { getUserOptions } from 'owa-session-store';
import { observer } from 'mobx-react';
import { getCalendarStrings, getTzOrUserTimeZone } from 'owa-datepicker-common';

const classNames = require('classnames');
const styles = require('./DropdownDatePicker.scss');

export interface DropdownDatePickerProps
    extends Omit<
        IDatePickerProps,
        | 'allowTextInput'
        | 'calendarProps'
        | 'componentRef'
        | 'dateTimeFormatter'
        | 'formatDate'
        | 'initialPickerDate'
        | 'isMonthPickerVisible'
        | 'maxDate'
        | 'minDate'
        | 'onSelectDate'
        | 'parseDateFromString'
        | 'showGoToToday'
        | 'showMonthPickerAsOverlay'
        | 'strings'
        | 'today'
        | 'value'
    > {
    initialPickerDate?: OwaDate;
    maxDate?: OwaDate;
    minDate?: OwaDate;
    onSelectDate: (date: OwaDate) => void;
    value: OwaDate | null | undefined;
    timeZoneId?: string;
    allowTextInput?: boolean;
}

/**
 * A dropdown date picker that is localized, formats dates according to OWA's date/time formats,
 * observes OWA options on how to show weeks and default to min/max dates supported by Outlook
 */
@observer
export class DropdownDatePicker extends React.Component<DropdownDatePickerProps> {
    public static defaultProps: Partial<DropdownDatePickerProps> = {
        maxDate: MAX_OUTLOOK_DATE,
        minDate: MIN_OUTLOOK_DATE,
    };

    // This is used to fix a callout dismissal bug in IE11 with React 16 and Fabric Contextual Menus
    private dateSelector = React.createRef<IDatePicker>();
    private onCalendarSelectWasCalled: boolean = false;

    public render() {
        const {
            className,
            disabled,
            initialPickerDate,
            maxDate,
            minDate,
            value,
            timeZoneId,
            allowTextInput = true,
            ...datePickerProps
        } = this.props;

        const { WeekStartDay, FirstWeekOfYear, ShowWeekNumbers } = getUserOptions();

        const strings = getCalendarStrings();
        const tzNow = owaDate(getTzOrUserTimeZone(value, timeZoneId), Date.now());

        const startOfMinDay = getLimitDate(minDate, MIN_JAVASCRIPT_DATE, startOfDay);
        const endOfMaxDay = getLimitDate(maxDate, MAX_JAVASCRIPT_DATE, endOfDay);
        const showGoToToday = isWithinRange(tzNow, startOfMinDay, endOfMaxDay);

        // Always display 6 weeks in calendar to ensure the height of
        // the control doesn't change as the user moves between months.
        // When clicking on "Today", select the date and close the calendar.
        const calendarProps = {
            onSelectDate: this.onCalendarSelect,
            selectDateOnClick: true,
            showSixWeeksByDefault: true,
            strings: strings,
        };

        const datePickerClass = classNames(styles.datepickerInput, className, {
            [styles.grey]: disabled,
        });

        return (
            <DatePicker
                {...datePickerProps}
                allowTextInput={allowTextInput}
                calendarProps={calendarProps}
                className={datePickerClass}
                componentRef={this.dateSelector}
                dateTimeFormatter={dateTimeFormatter}
                disabled={disabled}
                firstDayOfWeek={WeekStartDay}
                firstWeekOfYear={FirstWeekOfYear}
                formatDate={dateTimeFormatter.formatUserDate}
                initialPickerDate={asDate(initialPickerDate)}
                maxDate={asDate(maxDate)}
                minDate={asDate(minDate)}
                onAfterMenuDismiss={this.onAfterMenuDismissPreventIE11React16CalloutDismiss}
                onSelectDate={this.onSelectDate}
                parseDateFromString={this.parseDateFromString}
                showGoToToday={showGoToToday}
                showMonthPickerAsOverlay={true}
                showWeekNumbers={ShowWeekNumbers}
                strings={strings}
                today={asDate(tzNow)}
                value={asDate(value)}
            />
        );
    }

    private onSelectDate = (date: Date | null | undefined) => {
        if (date) {
            const { value, onSelectDate, timeZoneId } = this.props;
            const selectedDate = owaDate(
                getTzOrUserTimeZone(value, timeZoneId),
                date.getFullYear(),
                date.getMonth(),
                date.getDate()
            );
            onSelectDate(selectedDate);
        }
    };

    private parseDateFromString = (str: string) => {
        const { maxDate, minDate, value } = this.props;
        const parsedDate = parseUserDate(str, value);
        return parsedDate && isWithinRange(parsedDate, minDate, maxDate)
            ? asDate(parsedDate)
            : null;
    };

    private onCalendarSelect = () => {
        this.onCalendarSelectWasCalled = true;
    };

    /**
     * Callback issued when the menu is dimissed to ensure the focus goes back to the input after
     * the user interacts with the calendar menu
     */
    private onAfterMenuDismissPreventIE11React16CalloutDismiss = () => {
        if (this.onCalendarSelectWasCalled) {
            this.dateSelector.current.focus();
            this.onCalendarSelectWasCalled = false;
        }

        if (this.props.onAfterMenuDismiss) {
            this.props.onAfterMenuDismiss();
        }
    };
}

function getLimitDate(date: OwaDate, limit: OwaDate, limitFn: typeof startOfDay | typeof endOfDay) {
    return !date || isEqual(date, limit) ? limit : limitFn(date);
}
